import React from "react";
import { Tag, CreditCard, Banknote, MapPin, CarFront, CalendarDays } from "lucide-react";
import { STUDIO_ADDRESS } from "../../lib/services";

const PART_LABELS_CS = { morning: "dopoledne", afternoon: "odpoledne", full: "celý den" };

export default function StepSummary({ tier, serviceType, svc, vehicleType, vehicleBrandModel, extras, preferredDays, duration,
  basePrice, vehicleSurcharge, extrasTotal, travelFee, discount, total, promoCode, setPromoCode, promoStatus, validatePromo,
  paymentMethod, setPaymentMethod, locationType, serviceAddress, addressNote, submitError,
  additionalServices, vehicleTypes }) {
  return (
    <div className="grid md:grid-cols-2 gap-5">
      <div className="rounded-2xl bg-apex-card border border-apex-line p-5 space-y-3 text-sm text-apex-cream/90">
        <div className="text-apex-gold uppercase tracking-wider text-xs mb-2">Detaily</div>
        <div className="flex justify-between"><span>Služba</span><span className="font-semibold text-apex-cream">{svc.name}</span></div>
        <div className="flex justify-between"><span>Délka</span><span>{duration} min</span></div>
        <div className="pt-1" data-testid="summary-preferred-days">
          <div className="text-apex-cream/70 mb-1.5">Preferované dny:</div>
          <ul className="space-y-1">
            {preferredDays.map((pd) => {
              const dt = new Date(`${pd.date}T00:00:00`);
              return (
                <li key={pd.date} className="flex items-center gap-2">
                  <CalendarDays className="w-3.5 h-3.5 text-apex-gold flex-shrink-0" />
                  <span>{dt.toLocaleDateString("cs-CZ", { weekday: "short", day: "numeric", month: "numeric" })} — {PART_LABELS_CS[pd.part]}</span>
                </li>
              );
            })}
          </ul>
          <p className="mt-1.5 text-[11px] text-apex-cream/55">Konkrétní den a čas domluvíme telefonicky po odeslání žádosti.</p>
        </div>
        <div className="flex justify-between pt-1"><span>Vozidlo</span><span>{vehicleBrandModel} ({vehicleType})</span></div>
        <div className="pt-2 border-t border-apex-line" data-testid="summary-location">
          <div className="text-apex-cream/70 mb-1">Místo konání:</div>
          {locationType === "mobile" ? (
            <div className="flex items-start gap-2">
              <MapPin className="w-4 h-4 text-apex-gold mt-0.5 flex-shrink-0" />
              <div>
                <span className="text-apex-cream font-semibold">Mobilní služba</span> — {serviceAddress}
                {addressNote && <div className="text-xs text-apex-cream/60 mt-0.5">{addressNote}</div>}
              </div>
            </div>
          ) : (
            <div className="flex items-start gap-2">
              <CarFront className="w-4 h-4 text-apex-gold mt-0.5 flex-shrink-0" />
              <div><span className="text-apex-cream font-semibold">Přistavení u nás</span> — {STUDIO_ADDRESS.full}</div>
            </div>
          )}
        </div>
        {extras.filter(id => (additionalServices.find(a => a.id === id)?.price || 0) > 0).length > 0 && (
          <div className="pt-2 border-t border-apex-line">
            <div className="text-apex-cream/70 mb-1">Příplatky:</div>
            <ul>
              {extras.filter(id => (additionalServices.find(a => a.id === id)?.price || 0) > 0).map(id => {
                const e = additionalServices.find(a => a.id === id);
                return <li key={id} className="flex justify-between gap-2"><span>{e?.name}</span><span className="whitespace-nowrap">+{e?.price} Kč</span></li>;
              })}
            </ul>
          </div>
        )}
      </div>
      <div className="rounded-2xl bg-apex-card border border-apex-line p-5 space-y-3 text-sm text-apex-cream/90">
        <div className="text-apex-gold uppercase tracking-wider text-xs mb-2">Cena</div>
        <div className="flex justify-between"><span>Základní cena</span><span>{basePrice} Kč</span></div>
        {vehicleSurcharge > 0 && <div className="flex justify-between"><span>Příplatek za vůz</span><span>+{Math.round(vehicleSurcharge)} Kč</span></div>}
        {extrasTotal > 0 && <div className="flex justify-between"><span>Příplatkové služby</span><span>+{extrasTotal} Kč</span></div>}
        {travelFee > 0 && <div className="flex justify-between" data-testid="summary-travel-fee"><span>Dojezd (do 25 km)</span><span>+{travelFee} Kč</span></div>}
        {discount > 0 && <div className="flex justify-between text-emerald-300"><span>Sleva ({promoStatus.discount}%)</span><span>-{Math.round(discount)} Kč</span></div>}
        <div className="flex justify-between text-base pt-3 border-t border-apex-line">
          <span className="text-apex-cream font-semibold">Celkem</span>
          <span className="text-apex-gold text-xl font-display font-bold">{Math.round(total)} Kč</span>
        </div>

        <div className="pt-3">
          <div className="flex gap-2">
            <input value={promoCode} onChange={(e) => setPromoCode(e.target.value.toUpperCase())} placeholder="Promo kód" className="flex-1 px-3 py-2 rounded-lg bg-apex-bg border border-apex-line text-apex-cream text-sm" data-testid="booking-promo-input" />
            <button onClick={validatePromo} className="px-3 py-2 rounded-lg bg-apex-gold text-apex-bg text-sm font-semibold flex items-center gap-1" data-testid="booking-promo-btn"><Tag className="w-3.5 h-3.5" /> Ověřit</button>
          </div>
          {promoStatus?.valid && <div className="mt-2 text-xs text-emerald-300">{promoStatus.message}</div>}
          {promoStatus?.error && <div className="mt-2 text-xs text-red-400">{promoStatus.error}</div>}
        </div>

        <div className="pt-3">
          <div className="text-apex-cream/70 mb-2 text-xs">Způsob platby</div>
          <div className="grid grid-cols-2 gap-2">
            <button onClick={() => setPaymentMethod("online")} className={`rounded-lg border px-3 py-2 text-sm flex items-center gap-2 ${paymentMethod === "online" ? "bg-apex-gold text-apex-bg border-apex-gold" : "bg-apex-bg border-apex-line text-apex-cream"}`} data-testid="payment-online-btn">
              <CreditCard className="w-4 h-4" /> Online platba
            </button>
            <button onClick={() => setPaymentMethod("cash")} className={`rounded-lg border px-3 py-2 text-sm flex items-center gap-2 ${paymentMethod === "cash" ? "bg-apex-gold text-apex-bg border-apex-gold" : "bg-apex-bg border-apex-line text-apex-cream"}`} data-testid="payment-cash-btn">
              <Banknote className="w-4 h-4" /> Na místě
            </button>
          </div>
          <p className="mt-2 text-[11px] text-apex-cream/55">Platba se aktivuje po telefonické domluvě termínu.</p>
        </div>
      </div>
      {submitError && <div className="md:col-span-2 text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg p-3" data-testid="booking-error">{submitError}</div>}
    </div>
  );
}
